
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Users, Gift, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/context/LanguageContext";

interface InviteFriendsCardProps {
  userId: string;
}

const InviteFriendsCard: React.FC<InviteFriendsCardProps> = ({ userId }) => {
  const [inviteCode, setInviteCode] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [inviteStats, setInviteStats] = useState({
    totalInvites: 0,
    totalPoints: 0
  });
  const { toast } = useToast();
  const { language } = useLanguage();

  // 獲取用戶的邀請碼和統計資料
  useEffect(() => {
    fetchInviteData();
  }, [userId]);

  const fetchInviteData = async () => {
    try {
      setLoading(true);

      // 獲取用戶的邀請碼
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('invite_code')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('獲取邀請碼錯誤:', profileError);
        return;
      }

      if (profile?.invite_code) {
        setInviteCode(profile.invite_code);
      }

      // 獲取邀請統計資料
      const { data: invitedUsers, error: inviteError } = await supabase
        .from('profiles')
        .select('id')
        .eq('referrer_id', userId);

      if (!inviteError && invitedUsers) {
        const totalInvites = invitedUsers.length;
        
        // 計算邀請獲得的積分
        const { data: pointsData, error: pointsError } = await supabase
          .from('user_points_log')
          .select('points')
          .eq('user_id', userId)
          .eq('action_type', 'invite_friend');

        const totalPoints = pointsData?.reduce((sum, record) => sum + record.points, 0) || 0;

        setInviteStats({
          totalInvites,
          totalPoints
        });
      }

    } catch (error) {
      console.error('獲取邀請資料錯誤:', error);
    } finally {
      setLoading(false);
    }
  };

  // 生成邀請連結
  const generateInviteLink = () => {
    if (!inviteCode) return '';
    return `${window.location.origin}/signup?invite=${inviteCode}`;
  };

  const inviteLink = generateInviteLink();

  // 複製邀請連結
  const copyInviteLink = async () => {
    if (!inviteLink) {
      toast({
        variant: "destructive",
        title: "錯誤",
        description: "無法生成邀請連結",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      toast({
        title: "已複製連結",
        description: "邀請連結已複製到剪貼板",
      });
    } catch (error) {
      console.error("複製失敗:", error);
      toast({
        variant: "destructive",
        title: "複製失敗",
        description: "請手動複製連結",
      });
    }
  };

  // 複製邀請碼
  const copyInviteCode = async () => {
    if (!inviteCode) return;

    try {
      await navigator.clipboard.writeText(inviteCode);
      toast({
        title: "已複製邀請碼",
        description: "邀請碼已複製到剪貼板",
      });
    } catch (error) {
      console.error("複製失敗:", error);
      toast({
        variant: "destructive",
        title: "複製失敗",
        description: "請手動複製邀請碼",
      });
    }
  };

  // 分享邀請連結
  const shareInviteLink = () => {
    if (navigator.share) {
      navigator.share({
        title: '邀請你加入租屋評價平台',
        text: '我在使用這個租屋評價平台，邀請你一起來分享租屋經驗！',
        url: inviteLink
      }).catch(console.error);
    } else {
      // 降級到複製連結
      copyInviteLink();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {language === 'en' ? 'Invite Friends' : '邀請朋友'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          {language === 'en' ? 'Invite Friends' : '邀請朋友'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 邀請統計 */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600">{inviteStats.totalInvites}</div>
            <div className="text-sm text-blue-800">
              {language === 'en' ? 'Friends Invited' : '已邀請朋友'}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{inviteStats.totalPoints}</div>
            <div className="text-sm text-green-800">
              {language === 'en' ? 'Points Earned' : '獲得積分'}
            </div>
          </div>
        </div>

        {/* 邀請碼顯示 */}
        <div className="space-y-2">
          <Label htmlFor="invite-code">
            {language === 'en' ? 'Your Invite Code' : '您的邀請碼'}
          </Label>
          <div className="flex gap-2">
            <Input
              id="invite-code"
              value={inviteCode}
              readOnly
              className="font-mono text-center text-lg font-bold bg-gray-50"
            />
            <Button size="sm" onClick={copyInviteCode}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* 邀請連結 */}
        <div className="space-y-2">
          <Label htmlFor="invite-link">
            {language === 'en' ? 'Invite Link' : '邀請連結'}
          </Label>
          <div className="flex gap-2">
            <Input
              id="invite-link"
              value={inviteLink}
              readOnly
              className="text-xs bg-gray-50"
            />
            <Button size="sm" onClick={copyInviteLink}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* 分享按鈕 */}
        <div className="flex gap-2">
          <Button onClick={shareInviteLink} className="flex-1">
            <Share2 className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Share Invite' : '分享邀請'}
          </Button>
        </div>

        {/* 邀請說明 */}
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <Gift className="h-5 w-5 text-orange-600 mt-0.5" />
            <div className="text-sm text-orange-800">
              <p className="font-medium mb-1">
                {language === 'en' ? 'Invitation Rewards:' : '邀請獎勵說明：'}
              </p>
              <ul className="space-y-1 text-xs">
                <li>• {language === 'en' ? 'Friend joins with your code: +50 points' : '朋友使用您的邀請碼註冊：+50 積分'}</li>
                <li>• {language === 'en' ? 'Friend submits first review: +100 points' : '朋友完成首次投稿：+100 積分'}</li>
                <li>• {language === 'en' ? 'Share link clicks: +15 points daily' : '分享連結被點擊：每日 +15 積分'}</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InviteFriendsCard;
